import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { NsKey } from 'enums/NsKey';
import { Button } from 'components/ui/Button';
import { GenericLoading, Sizes } from 'components/ui/Loader';
import { DownloadIcon, PrintIcon, XMarkIcon } from 'components/icons/Icons';
import {
  Maximize2,
  Minimize2,
  ZoomIn,
  ZoomOut,
  RotateCw,
  ChevronLeft,
  ChevronRight,
  FileText
} from 'lucide-react';
import { DocumentUrlDto } from 'api/files';
import clsx from 'clsx';

export type DocumentType = 'pdf' | 'image';

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];

const getDocumentTypeFromFileName = (fileName?: string): DocumentType => {
  if (!fileName) return 'pdf';
  const extension = fileName.split('.').pop()?.toLowerCase() || '';
  if (IMAGE_EXTENSIONS.includes(extension)) return 'image';
  return 'pdf';
};

const getExtensionFromFileName = (fileName?: string): string => {
  if (!fileName) return 'pdf';
  return fileName.split('.').pop()?.toLowerCase() || 'pdf';
};

const getMimeTypeFromExtension = (extension: string): string => {
  const mimeTypes: Record<string, string> = {
    pdf: 'application/pdf',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
    bmp: 'image/bmp'
  };
  return mimeTypes[extension] || 'application/pdf';
};

export interface MultiDocumentViewerProps {
  title: string;
  documents: DocumentUrlDto[];
  onClose: () => void;
}

export const MultiDocumentViewer: FC<MultiDocumentViewerProps> = ({
  title,
  documents,
  onClose
}) => {
  const { t } = useTranslation(NsKey.Common);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [scale, setScale] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentBlobUrl, setCurrentBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Cache des blob URLs par index
  const blobCacheRef = useRef<Map<number, string>>(new Map());
  const loadingIndexRef = useRef<number | null>(null);

  const currentDocument = documents[currentIndex];
  const documentType = getDocumentTypeFromFileName(currentDocument?.fileName);
  const fileExtension = getExtensionFromFileName(currentDocument?.fileName);

  // Charger le document courant
  useEffect(() => {
    let cancelled = false;

    const loadDocument = async () => {
      // Vérifier le cache
      const cachedUrl = blobCacheRef.current.get(currentIndex);
      if (cachedUrl) {
        setCurrentBlobUrl(cachedUrl);
        setIsLoading(false);
        setError(null);
        return;
      }

      const doc = documents[currentIndex];
      if (!doc?.url) {
        setCurrentBlobUrl(null);
        setIsLoading(false);
        setError(t('COMMON.ERRORS.DOCUMENT_NOT_FOUND'));
        return;
      }

      // Éviter les chargements multiples du même index
      if (loadingIndexRef.current === currentIndex) {
        return;
      }

      loadingIndexRef.current = currentIndex;
      setIsLoading(true);
      setError(null);
      setCurrentBlobUrl(null);

      try {
        // Télécharger le fichier depuis l'URL Azure
        const response = await fetch(doc.url);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        if (cancelled) return;

        const originalBlob = await response.blob();
        const extension = getExtensionFromFileName(doc.fileName);
        const mimeType = getMimeTypeFromExtension(extension);

        // Forcer le bon type MIME pour l'affichage
        const typedBlob = new Blob([originalBlob], { type: mimeType });
        const blobUrl = URL.createObjectURL(typedBlob);

        // Sauvegarder dans le cache
        blobCacheRef.current.set(currentIndex, blobUrl);

        if (!cancelled) {
          setCurrentBlobUrl(blobUrl);
          setError(null);
        }
      } catch (err) {
        console.error('Error fetching document:', err);
        if (!cancelled) {
          setError(t('COMMON.ERRORS.DOCUMENT_LOAD_ERROR'));
        }
      } finally {
        if (loadingIndexRef.current === currentIndex) {
          loadingIndexRef.current = null;
        }
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadDocument();

    return () => {
      cancelled = true;
    };
  }, [currentIndex, documents, t]);

  // Cleanup des blob URLs au démontage
  useEffect(() => {
    return () => {
      blobCacheRef.current.forEach((url) => {
        URL.revokeObjectURL(url);
      });
      blobCacheRef.current.clear();
    };
  }, []);

  const handleZoomIn = useCallback(() => {
    setScale((prev) => Math.min(prev + 25, 200));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale((prev) => Math.max(prev - 25, 50));
  }, []);

  const handleRotate = useCallback(() => {
    setRotation((prev) => (prev + 90) % 360);
  }, []);

  const handleDownload = useCallback(() => {
    if (currentBlobUrl) {
      const link = document.createElement('a');
      link.href = currentBlobUrl;
      link.download = currentDocument?.fileName || `document.${fileExtension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [currentBlobUrl, currentDocument, fileExtension]);

  const handlePrint = useCallback(() => {
    if (currentBlobUrl) {
      const printWindow = window.open(currentBlobUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    }
  }, [currentBlobUrl]);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => !prev);
  }, []);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
    setScale(100);
    setRotation(0);
  }, []);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => Math.min(documents.length - 1, prev + 1));
    setScale(100);
    setRotation(0);
  }, [documents.length]);

  const selectDocument = useCallback((index: number) => {
    setCurrentIndex(index);
    setScale(100);
    setRotation(0);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isFullscreen) {
          setIsFullscreen(false);
        } else {
          onClose();
        }
      } else if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen, onClose, goToPrevious, goToNext]);

  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isFullscreen]);

  const renderSidebar = () => (
    <div className="w-64 border-r border-gray-200 bg-gray-50 flex flex-col shrink-0">
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700">
          {t('COMMON.LABELS.DOCUMENTS')} ({documents.length})
        </h3>
      </div>
      <div className="flex-1 overflow-y-auto p-2">
        {documents.map((doc, index) => (
          <button
            key={index}
            onClick={() => selectDocument(index)}
            className={clsx(
              'w-full text-left p-3 rounded-lg mb-2 flex items-center gap-3 transition-colors',
              currentIndex === index
                ? 'bg-primary/10 border border-primary'
                : 'bg-white border border-gray-200 hover:bg-gray-100'
            )}
          >
            <FileText
              className={clsx(
                'w-5 h-5 shrink-0',
                currentIndex === index ? 'text-primary' : 'text-gray-400'
              )}
            />
            <span
              className={clsx(
                'text-sm truncate',
                currentIndex === index
                  ? 'text-primary font-medium'
                  : 'text-gray-700'
              )}
            >
              {doc.fileName || `Document ${index + 1}`}
            </span>
          </button>
        ))}
      </div>
    </div>
  );

  const renderHeader = (isInFullscreen: boolean) => (
    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50 shrink-0">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-semibold text-gray-900 truncate max-w-md">
          {title}
        </h2>
        {documents.length > 1 && (
          <span className="text-sm text-gray-500">
            ({currentIndex + 1}/{documents.length})
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        {documents.length > 1 && (
          <div className="flex items-center gap-1 mr-2">
            <button
              onClick={goToPrevious}
              disabled={currentIndex === 0}
              className="p-2 hover:bg-gray-100 rounded-lg border border-gray-200 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
              title={t('COMMON.ACTIONS.PREVIOUS')}
            >
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            </button>
            <button
              onClick={goToNext}
              disabled={currentIndex === documents.length - 1}
              className="p-2 hover:bg-gray-100 rounded-lg border border-gray-200 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
              title={t('COMMON.ACTIONS.NEXT')}
            >
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        )}

        <div className="flex items-center gap-1 px-2 py-1 bg-white rounded-lg border border-gray-200">
          <button
            onClick={handleZoomOut}
            disabled={scale <= 50}
            className="p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            title={t('COMMON.ACTIONS.ZOOM_OUT')}
          >
            <ZoomOut className="w-4 h-4 text-gray-600" />
          </button>
          <span className="text-sm text-gray-600 min-w-[3rem] text-center">
            {scale}%
          </span>
          <button
            onClick={handleZoomIn}
            disabled={scale >= 200}
            className="p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            title={t('COMMON.ACTIONS.ZOOM_IN')}
          >
            <ZoomIn className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        <button
          onClick={handleRotate}
          className="p-2 hover:bg-gray-100 rounded-lg border border-gray-200 bg-white"
          title={t('COMMON.ACTIONS.ROTATE')}
        >
          <RotateCw className="w-4 h-4 text-gray-600" />
        </button>

        <button
          onClick={toggleFullscreen}
          className="p-2 hover:bg-gray-100 rounded-lg border border-gray-200 bg-white"
          title={
            isInFullscreen
              ? t('COMMON.ACTIONS.EXIT_FULLSCREEN')
              : t('COMMON.ACTIONS.FULLSCREEN')
          }
        >
          {isInFullscreen ? (
            <Minimize2 className="w-4 h-4 text-gray-600" />
          ) : (
            <Maximize2 className="w-4 h-4 text-gray-600" />
          )}
        </button>

        <button
          onClick={isInFullscreen ? toggleFullscreen : onClose}
          className="p-2 hover:bg-gray-100 rounded-lg border border-gray-200 bg-white ml-2"
          title={t('COMMON.ACTIONS.CLOSE')}
        >
          <XMarkIcon className="w-5 h-5 text-gray-600" />
        </button>
      </div>
    </div>
  );

  const renderPdfContent = () => {
    if (!currentBlobUrl) return null;

    return (
      <iframe
        key={`pdf-${currentIndex}-${scale}`}
        src={`${currentBlobUrl}#toolbar=0&navpanes=0&zoom=${scale}`}
        className="bg-white shadow-lg w-full h-full"
        style={{ border: 'none' }}
        title={currentDocument?.fileName || 'Document'}
      />
    );
  };

  const renderImageContent = () => (
    <img
      src={currentBlobUrl || ''}
      alt={currentDocument?.fileName || 'Document'}
      className="max-w-full max-h-full object-contain shadow-lg bg-white"
      style={{
        transform: `scale(${scale / 100}) rotate(${rotation}deg)`,
        transition: 'transform 0.3s ease'
      }}
    />
  );

  const renderContent = () => (
    <div className="flex-1 relative bg-gray-100 overflow-auto">
      {isLoading ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <GenericLoading size={Sizes.lg} loading />
            <p className="text-gray-600">{t('COMMON.LABELS.LOADING')}</p>
          </div>
        </div>
      ) : error ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <FileText className="w-16 h-16 text-gray-300" />
            <p className="text-gray-500">{error}</p>
          </div>
        </div>
      ) : currentBlobUrl ? (
        <div
          className="w-full h-full flex items-center justify-center p-4"
          style={
            documentType === 'pdf'
              ? {
                  transform: `rotate(${rotation}deg)`,
                  transition: 'transform 0.3s ease'
                }
              : undefined
          }
        >
          {documentType === 'pdf' ? renderPdfContent() : renderImageContent()}
        </div>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <p className="text-gray-500">
            {t('COMMON.ERRORS.DOCUMENT_NOT_FOUND')}
          </p>
        </div>
      )}
    </div>
  );

  const renderFooter = () => (
    <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 shrink-0">
      <div className="text-sm text-gray-600 truncate max-w-md">
        {currentDocument?.fileName || ''}
      </div>
      <div className="flex items-center gap-3">
        <Button outline onClick={handleDownload} disabled={!currentBlobUrl}>
          <DownloadIcon className="w-4 h-4 mr-2" />
          {t('COMMON.ACTIONS.DOWNLOAD')}
        </Button>
        <Button outline onClick={handlePrint} disabled={!currentBlobUrl}>
          <PrintIcon className="w-4 h-4 mr-2" />
          {t('COMMON.ACTIONS.PRINT')}
        </Button>
      </div>
    </div>
  );

  const renderEmptyState = () => (
    <div className="flex flex-col items-center justify-center h-full p-8">
      <FileText className="w-16 h-16 text-gray-300 mb-4" />
      <h3 className="text-lg font-medium text-gray-700 mb-2">
        {t('COMMON.LABELS.NO_DOCUMENTS')}
      </h3>
      <p className="text-sm text-gray-500 text-center">
        {t('COMMON.LABELS.NO_DOCUMENTS_DESCRIPTION')}
      </p>
      <Button className="mt-6" onClick={onClose}>
        {t('COMMON.ACTIONS.CLOSE')}
      </Button>
    </div>
  );

  if (documents.length === 0) {
    return (
      <div className="flex flex-col bg-white rounded-2xl overflow-hidden h-[80vh]">
        {renderHeader(false)}
        {renderEmptyState()}
      </div>
    );
  }

  const mainContent = (
    <div className="flex flex-1 overflow-hidden">
      {documents.length > 1 && renderSidebar()}
      <div className="flex-1 flex flex-col">{renderContent()}</div>
    </div>
  );

  if (isFullscreen) {
    return (
      <>
        <div className="hidden" />
        {createPortal(
          <div className="fixed inset-0 z-[9999] flex flex-col bg-white">
            {renderHeader(true)}
            {mainContent}
            {renderFooter()}
          </div>,
          document.body
        )}
      </>
    );
  }

  return (
    <div className="flex flex-col bg-white rounded-2xl overflow-hidden h-[80vh]">
      {renderHeader(false)}
      {mainContent}
      {renderFooter()}
    </div>
  );
};
